import { defineStore } from 'pinia';
import { ref } from 'vue';
import _ from 'lodash';
import { generateFilters, type CodeColumn, type FilterFunc } from '@/stores/codeQueryStore.helper';

export const useCodeQueryStore = defineStore('codeQuery', () => {
    const libraryName = ref<string | undefined>(undefined);
    const columns = ref<CodeColumn[]>([]);
    // Queries are lists of [field, operator, value] tuples, used to filter codes
    const queries = ref<string[][]>([]);
    const regexs = ref<string[]>([]);
    const filters = ref<FilterFunc[]>([]);

    const setLibraryName = (name: string) => {
        libraryName.value = name;
    };

    const updateFilter = () => {
        filters.value = generateFilters(regexs.value, queries.value, columns.value);
    };

    const addColumns = (newColumns: CodeColumn[]) => {
        columns.value = [...newColumns];
        updateFilter();
    };

    const addQuery = (query: string[]) => {
        if (queries.value.find(q => _.isEqual(q, query))) return;
        queries.value.push(query);
        updateFilter();
    };

    const addQueries = (qs: string[][]) => {
        if (!Array.isArray(qs)) return;
        if (_.isEqual(queries.value, qs)) return;

        queries.value = [];

        qs.forEach((newQuery) => {
            addQuery(newQuery);
        });
        updateFilter();
    };

    const removeQuery = (query: string[]) => {
        const match = queries.value.find(q => _.isEqual(q, query));
        if (match) {
            queries.value = _.without(queries.value, match);
        }

        updateFilter();
    };

    const addRegex = (reg: string) => {
        if (regexs.value.find(r => r === reg)) return;
        regexs.value.push(reg);
        updateFilter();
    };

    const addRegexs = (reg: string[]) => {
        if (!Array.isArray(reg)) return;
        if (_.isEqual(regexs.value, reg)) return;

        regexs.value = [];

        reg.forEach((newRegex) => {
            addRegex(newRegex);
        });
        updateFilter();
    };

    const removeRegex = (reg: string) => {
        regexs.value = _.without(regexs.value, reg);
        updateFilter();
    };

    const reset = () => {
        libraryName.value = '';
        queries.value = [];
        filters.value = [];
        regexs.value = [];
    };

    return {
        // Reactive elements
        libraryName,
        columns,
        queries,
        filters,
        regexs,

        // actions
        setLibraryName,
        addColumns,

        // Queries
        addQuery,
        addQueries,
        removeQuery,

        // RegEx
        addRegex,
        addRegexs,
        removeRegex,

        updateFilter,

        reset
    };
});
